import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Box,
  Typography,
  Grid,
  Paper
} from '@mui/material';
import { Plus, Trash2, X } from 'lucide-react';

const ManualMethodEntry = ({ onAddMethod }) => {
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState({
    name: '',
    description: '',
    type: 'rest',
    httpMethod: 'GET',
    path: '',
    parameters: [],
    responses: [{
      statusCode: '200',
      description: 'Success Response',
      parameters: []
    }]
  });

  const [parameter, setParameter] = useState({
    name: '',
    type: 'string',
    description: '',
    required: false,
    isArray: false,
    in: 'query'
  });

  const handleAddParameter = () => {
    if (parameter.name) {
      setMethod(prev => ({
        ...prev,
        parameters: [...prev.parameters, { ...parameter }]
      }));
      setParameter({
        name: '',
        type: 'string',
        description: '',
        required: false,
        isArray: false,
        in: 'query'
      });
    }
  };

  const handleRemoveParameter = (index) => {
    setMethod(prev => ({
      ...prev,
      parameters: prev.parameters.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = () => {
    onAddMethod(method);
    setOpen(false);
    setMethod({
      name: '',
      description: '',
      type: 'rest',
      httpMethod: 'GET',
      path: '',
      parameters: [],
      responses: [{
        statusCode: '200',
        description: 'Success Response',
        parameters: []
      }]
    });
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<Plus />}
        onClick={() => setOpen(true)}
        sx={{ mb: 2 }}
      >
        Add Method
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#f8f4ff',
          }
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            bgcolor: '#edbc45',
            color: '#000',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontWeight: 600
          }}
        >
          Add New REST Method
          <IconButton
            aria-label="close"
            onClick={() => setOpen(false)}
            sx={{
              color: '#000',
              p: 0.5,
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.1)',
              }
            }}
          >
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Method Name"
                value={method.name}
                onChange={(e) => setMethod(prev => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>HTTP Method</InputLabel>
                <Select
                  value={method.httpMethod}
                  label="HTTP Method"
                  onChange={(e) => setMethod(prev => ({ ...prev, httpMethod: e.target.value }))}
                >
                  <MenuItem value="GET">GET</MenuItem>
                  <MenuItem value="POST">POST</MenuItem>
                  <MenuItem value="PUT">PUT</MenuItem>
                  <MenuItem value="DELETE">DELETE</MenuItem>
                  <MenuItem value="PATCH">PATCH</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Path"
                value={method.path}
                onChange={(e) => setMethod(prev => ({ ...prev, path: e.target.value }))}
                placeholder="/api/resource/{id}"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={method.description}
                onChange={(e) => setMethod(prev => ({ ...prev, description: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>Parameters</Typography>
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="Name"
                      value={parameter.name}
                      onChange={(e) => setParameter(prev => ({ ...prev, name: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth>
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={parameter.type}
                        label="Type"
                        onChange={(e) => setParameter(prev => ({ ...prev, type: e.target.value }))}
                      >
                        <MenuItem value="string">String</MenuItem>
                        <MenuItem value="number">Number</MenuItem>
                        <MenuItem value="boolean">Boolean</MenuItem>
                        <MenuItem value="object">Object</MenuItem>
                        <MenuItem value="array">Array</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth>
                      <InputLabel>Location</InputLabel>
                      <Select
                        value={parameter.in}
                        label="Location"
                        onChange={(e) => setParameter(prev => ({ ...prev, in: e.target.value }))}
                      >
                        <MenuItem value="query">Query</MenuItem>
                        <MenuItem value="path">Path</MenuItem>
                        <MenuItem value="header">Header</MenuItem>
                        <MenuItem value="body">Body</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleAddParameter}
                      startIcon={<Plus />}
                      sx={{
                        backgroundColor: '#edbc45',
                        color: '#000',
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: '#d4a63a'
                        }
                      }}
                    >
                      Add
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              {method.parameters.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Added Parameters:</Typography>
                  {method.parameters.map((param, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {param.name} ({param.type}) - {param.in}
                      </Typography>
                      <IconButton size="small" onClick={() => handleRemoveParameter(index)}>
                        <Trash2 size={16} />
                      </IconButton>
                    </Box>
                  ))}
                </Paper>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setOpen(false)}
            variant="outlined"
            sx={{ color: '#000', borderColor: '#000' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              backgroundColor: '#edbc45',
              color: '#000',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#d4a63a'
              }
            }}
          >
            Add Method
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManualMethodEntry;